import React, { ReactElement, useContext, useEffect, useState } from 'react'
import Image from 'next/image'
import { Context } from '@/components/Context'
import Loader from '@/components/Loader'
import { Image as Img } from '@/database/image'
import handleViewport, { InjectedViewportProps } from 'react-in-viewport'

const getMaximumWidth = (image: Img | undefined): number => {
    if (image && window.innerWidth < image.width) {
        return window.innerWidth
    } else if (image) {
        return image.width
    }
    return 350
}

const LoadImageOnDemand = ({
    id,
    imageId,
    style,
    alt,
    objectFit,
    layout,
    link,
    useFill,
}: {
    id?: string
    imageId: number
    style?: string
    alt?: string
    layout?: any
    objectFit?: any
    link?: string
    useFill?: boolean
}): ReactElement => {
    const { fetchImage, getCachedImage } = useContext(Context)
    const [image, setImage] = React.useState(getCachedImage(imageId))
    const [imageRequest, setImageRequest] = useState(false)
    const maximumWidth = getMaximumWidth(image)
    const [requestedOnce, setRequestedOnce] = useState(false)

    const Block = (props: InjectedViewportProps<HTMLDivElement>) => {
        const { forwardedRef } = props
        return (
            <div ref={forwardedRef} className="viewport-block">
                {image && image?.data ? (
                    link ? (
                        <a
                            href={link}
                            style={
                                useFill
                                    ? { display: 'block', width: '100%', height: '100%', position: 'relative' }
                                    : undefined
                            }
                        >
                            {useFill ? (
                                <Image
                                    id={id ?? imageId.toString()}
                                    alt={alt ?? ''}
                                    src={image.data}
                                    fill
                                    style={{ objectFit: objectFit ?? 'cover' }}
                                    className={style}
                                />
                            ) : (
                                <Image
                                    id={id ?? imageId.toString()}
                                    alt={alt ?? ''}
                                    src={image.data}
                                    width={image.width}
                                    height={image.height}
                                    style={{ objectFit: objectFit ?? 'contain' }}
                                    className={style}
                                />
                            )}
                        </a>
                    ) : useFill ? (
                        <Image
                            id={id ?? imageId.toString()}
                            alt={alt ?? ''}
                            src={image.data}
                            fill
                            style={{ objectFit: objectFit ?? 'cover' }}
                            className={style}
                        />
                    ) : (
                        <Image
                            id={id ?? imageId.toString()}
                            alt={alt ?? ''}
                            src={image.data}
                            width={image.width}
                            height={image.height}
                            style={{ objectFit: objectFit ?? 'contain' }}
                            className={style}
                        />
                    )
                ) : (
                    <Loader
                        width={maximumWidth}
                        height={image ? (image.height * maximumWidth) / image.width : maximumWidth}
                    />
                )}
            </div>
        )
    }

    const ViewportBlock = handleViewport(Block)

    useEffect(() => {
        const getImage = async () => {
            return await fetchImage(imageId)
        }

        if (!requestedOnce && imageRequest && !image) {
            getImage().then((image) => {
                setRequestedOnce(true)
                if (image?.data) {
                    setImage(image)
                }
            })
        }
    }, [imageRequest])

    return (
        <ViewportBlock
            onEnterViewport={() => {
                if (!image?.data) {
                    setImageRequest(true)
                }
            }}
        />
    )
}

export default LoadImageOnDemand
